import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { apiRequest } from '../lib/api';
import { useData } from '../context/DataContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLoader, FiCheckCircle, FiAlertCircle, FiArrowLeft, FiShield } = FiIcons;

const PaymentVerification = ({ sessionId }) => {
  const navigate = useNavigate();
  const { forceRefresh } = useData();
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('Confirming transaction with Stripe servers...');
  const apiCalledRef = useRef(false);

  useEffect(() => {
    if (!sessionId || apiCalledRef.current) return;
    apiCalledRef.current = true;

    const verifyPayment = async () => {
      try {
        const response = await apiRequest('/verify-payment-session', {
          body: JSON.stringify({ sessionId }),
        });

        if (response.success) {
          setStatus('success');
          setMessage('Payment verified! Your student ledger has been updated in real-time.');
          forceRefresh();
        } else {
          throw new Error(response.message || "Verification failed");
        }
      } catch (error) {
        console.error('Payment verification error:', error);
        setStatus('error');
        setMessage(error.message || 'We could not confirm this payment. Please contact support.');
      }
    };

    verifyPayment();
  }, [sessionId, forceRefresh]);

  return (
    <div className="fixed inset-0 bg-gray-50 flex items-center justify-center p-4 z-[200]">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="bg-white rounded-[3rem] shadow-2xl max-w-md w-full text-center p-12 border border-gray-100"
      >
        <div className="mx-auto mb-8 w-24 h-24 flex items-center justify-center rounded-[2rem] bg-gray-50 relative">
          {status === 'verifying' ? (
            <>
              <div className="absolute inset-0 border-4 border-indigo-100 rounded-[2rem]"></div>
              <div className="absolute inset-0 border-4 border-indigo-600 rounded-[2rem] border-t-transparent animate-spin"></div>
              <SafeIcon icon={FiShield} className="text-4xl text-indigo-600" />
            </>
          ) : status === 'success' ? (
            <div className="w-full h-full bg-emerald-50 rounded-[2rem] flex items-center justify-center">
              <SafeIcon icon={FiCheckCircle} className="text-5xl text-emerald-500" />
            </div>
          ) : (
            <div className="w-full h-full bg-rose-50 rounded-[2rem] flex items-center justify-center">
              <SafeIcon icon={FiAlertCircle} className="text-5xl text-rose-500" />
            </div>
          )}
        </div>

        <h2 className="text-3xl font-black text-gray-900 tracking-tighter uppercase">
          {status === 'verifying' ? 'Verifying' : status === 'success' ? 'Confirmed' : 'Payment Issue'}
        </h2>
        <p className="text-gray-500 font-bold mt-4 mb-10 leading-relaxed">{message}</p>

        {status !== 'verifying' && (
          <button 
            onClick={() => navigate('/', { replace: true })} 
            className="flex items-center justify-center gap-3 w-full py-5 bg-gray-900 text-white rounded-2xl hover:bg-black transition-all font-black text-xs uppercase tracking-widest shadow-xl"
          >
            <SafeIcon icon={FiArrowLeft} /> Return to Dashboard
          </button>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentVerification;